// src/pages/ReservationDetail.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { supabase } from '../services/supabaseClient';
import ForfeoReservationPassPanel from '../components/network/ForfeoReservationPassPanel';
import {
  FaArrowLeft,
  FaCalendarCheck,
  FaCheckCircle,
  FaEnvelope,
  FaExclamationTriangle,
  FaExternalLinkAlt,
  FaMapMarkerAlt,
  FaPaperPlane,
  FaPhone,
  FaTimesCircle,
} from 'react-icons/fa';

type Reservation = {
  id: string;
  status?: string | null;
  admin_status?: string | null;
  public_status?: string | null;
  customer_email?: string | null;
  customer_name?: string | null;
  customer_phone?: string | null;
  party_size?: number | null;
  slot_start?: string | null;
  slot_end?: string | null;
  created_at?: string | null;
  cancelled_at?: string | null;
  customer_cancelled_at?: string | null;
  total_price?: number | null;
  locked_price?: number | null;
  deal?: any;
  business?: any;
};

const moneyCad = (value?: number | null) => {
  const n = Number(value || 0);
  if (!Number.isFinite(n) || n <= 0) return 'Prix confirmé';
  return new Intl.NumberFormat('fr-CA', { style: 'currency', currency: 'CAD', maximumFractionDigits: 2 }).format(n);
};

const dateTime = (value?: string | null) => {
  if (!value) return 'À confirmer';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return 'À confirmer';
  return new Intl.DateTimeFormat('fr-CA', { dateStyle: 'full', timeStyle: 'short' }).format(d);
};

const effectiveStatus = (r?: Reservation | null) => String(r?.admin_status || r?.public_status || r?.status || 'confirmed').toLowerCase();
const isCancelled = (r?: Reservation | null) => effectiveStatus(r).includes('cancel');

const statusText = (r?: Reservation | null) => {
  const s = effectiveStatus(r);
  if (s.includes('cancel')) return 'Réservation annulée';
  if (s.includes('no_show')) return 'No-show';
  if (s.includes('completed') || s.includes('honor') || s.includes('checked')) return 'Expérience complétée';
  return 'Réservation confirmée';
};

export default function ReservationDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [reservation, setReservation] = useState<Reservation | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    const init = async () => {
      const { data } = await supabase.auth.getUser();
      const sessionEmail = data?.user?.email || '';
      if (sessionEmail) setEmail(sessionEmail);
      await loadReservation(sessionEmail);
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const invokeSelfService = async (body: any) => {
    const { data, error } = await supabase.functions.invoke('reservation-self-service', { body });
    if (error) throw new Error(error.message || 'Erreur Edge Function');
    if (data?.ok === false) throw new Error(data?.error || 'Action impossible');
    return data;
  };

  const loadReservation = async (forcedEmail?: string) => {
    if (!id) return;
    const emailToUse = (forcedEmail || email || '').trim();
    setLoading(true);
    setMessage(null);
    try {
      const data = await invokeSelfService({ action: 'get', reservation_id: id, email: emailToUse || undefined });
      setReservation(data?.reservation || null);
    } catch (err: any) {
      setMessage({ type: 'error', text: err?.message || 'Impossible de charger cette réservation.' });
    } finally {
      setLoading(false);
    }
  };

  const resendConfirmation = async () => {
    if (!id) return;
    setActionLoading(true);
    setMessage(null);
    try {
      await invokeSelfService({ action: 'resend_confirmation', reservation_id: id, email });
      setMessage({ type: 'success', text: 'Le courriel de confirmation a été renvoyé.' });
    } catch (err: any) {
      setMessage({ type: 'error', text: err?.message || 'Renvoi impossible.' });
    } finally {
      setActionLoading(false);
    }
  };

  const cancelReservation = async () => {
    if (!id) return;
    const reason = window.prompt('Raison de l’annulation (optionnel) :') || '';
    if (!window.confirm('Confirmer l’annulation de cette réservation Last Call ?')) return;
    setActionLoading(true);
    setMessage(null);
    try {
      await invokeSelfService({ action: 'cancel', reservation_id: id, email, reason });
      setMessage({ type: 'success', text: 'Votre réservation a été annulée.' });
      await loadReservation(email);
    } catch (err: any) {
      setMessage({ type: 'error', text: err?.message || 'Annulation impossible.' });
    } finally {
      setActionLoading(false);
    }
  };

  const mapsUrl = useMemo(() => {
    const b = reservation?.business || {};
    const q = `${b.name || ''} ${b.address || ''} ${b.city || ''} ${b.region || ''}`.trim();
    return q ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}` : '';
  }, [reservation]);

  const price = moneyCad(Number(reservation?.locked_price || reservation?.total_price || reservation?.deal?.discount_price || 0));

  return (
    <div className="min-h-screen bg-slate-50 font-sans text-slate-900">
      <Header />
      <main className="px-4 pb-24 pt-28 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-5xl">
          <button onClick={() => navigate('/mes-reservations')} className="mb-6 inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-sm font-black text-slate-600 shadow-sm hover:bg-slate-100">
            <FaArrowLeft /> Retour à mes réservations
          </button>

          <div className="overflow-hidden rounded-[2rem] bg-slate-950 text-white shadow-2xl">
            <div className="relative p-8 md:p-10">
              <div className="absolute -right-20 -top-20 h-64 w-64 rounded-full bg-red-500/20 blur-3xl" />
              <div className="relative">
                <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/10 px-4 py-2 text-[10px] font-black uppercase tracking-[0.24em] text-red-100">
                  <FaCalendarCheck /> Last Call
                </div>
                <h1 className="text-3xl font-black tracking-tight md:text-5xl">Détails de réservation</h1>
                <p className="mt-3 max-w-2xl text-sm font-semibold leading-6 text-slate-300">Retrouve ton prix verrouillé, le partenaire révélé, l’itinéraire et les actions rapides.</p>
              </div>
            </div>
          </div>

          <div className="mt-6 rounded-3xl border border-slate-100 bg-white p-5 shadow-sm">
            <div className="grid gap-3 md:grid-cols-[1fr_auto] md:items-end">
              <div>
                <label className="mb-2 block text-[10px] font-black uppercase tracking-widest text-slate-400">Courriel associé à la réservation</label>
                <div className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
                  <FaEnvelope className="text-slate-400" />
                  <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="votre@email.com" className="w-full bg-transparent font-bold outline-none" />
                </div>
              </div>
              <button onClick={() => loadReservation(email)} className="rounded-2xl bg-slate-950 px-6 py-4 text-sm font-black text-white hover:bg-slate-800">Rechercher</button>
            </div>
          </div>

          {message && (
            <div className={`mt-5 rounded-2xl border p-4 text-sm font-bold ${message.type === 'success' ? 'border-green-100 bg-green-50 text-green-700' : 'border-red-100 bg-red-50 text-red-700'}`}>
              {message.text}
            </div>
          )}

          {reservation && !loading && (
            <ForfeoReservationPassPanel
              id={reservation.id}
              kind="reservation"
              title="Votre pass Forfeo est prêt à suivre cette réservation"
              subtitle="Forfeo prépare l’expérience mobile complète : wallet, QR sécurisé, statut de validation et support après réservation."
              className="mt-6"
            />
          )}

          {loading ? (
            <div className="mt-6 rounded-3xl bg-white p-12 text-center shadow-sm">
              <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-4 border-slate-200 border-t-red-600" />
              <p className="font-bold text-slate-500">Chargement...</p>
            </div>
          ) : reservation ? (
            <div className="mt-6 grid gap-6 lg:grid-cols-[1.1fr_0.9fr]">
              <section className="rounded-[2rem] border border-slate-100 bg-white p-6 shadow-sm">
                <div className="mb-5 flex flex-wrap items-center justify-between gap-4">
                  <div>
                    <div className={`mb-2 inline-flex items-center gap-2 rounded-full px-3 py-1 text-[10px] font-black uppercase tracking-widest ${isCancelled(reservation) ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}>
                      {isCancelled(reservation) ? <FaTimesCircle /> : <FaCheckCircle />} {statusText(reservation)}
                    </div>
                    <h2 className="text-2xl font-black text-slate-950">{reservation.deal?.title || 'Offre Last Call'}</h2>
                    <p className="mt-1 text-sm font-semibold text-slate-500">Réservation #{String(reservation.id).slice(0, 8)}</p>
                  </div>
                  <div className="rounded-2xl bg-red-50 px-5 py-4 text-right">
                    <div className="text-[10px] font-black uppercase tracking-widest text-red-400">Prix verrouillé</div>
                    <div className="text-2xl font-black text-red-700">{price}</div>
                  </div>
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <div className="rounded-2xl bg-slate-50 p-4">
                    <div className="text-[10px] font-black uppercase tracking-widest text-slate-400">Date</div>
                    <div className="mt-1 font-black text-slate-900">{dateTime(reservation.slot_start || reservation.created_at)}</div>
                  </div>
                  <div className="rounded-2xl bg-slate-50 p-4">
                    <div className="text-[10px] font-black uppercase tracking-widest text-slate-400">Personnes</div>
                    <div className="mt-1 font-black text-slate-900">{reservation.party_size || 1}</div>
                  </div>
                  <div className="rounded-2xl bg-slate-50 p-4 md:col-span-2">
                    <div className="text-[10px] font-black uppercase tracking-widest text-slate-400">Client</div>
                    <div className="mt-1 font-black text-slate-900">{reservation.customer_name || 'Voyageur Forfeo'}</div>
                    <div className="mt-1 text-sm font-semibold text-slate-500">{reservation.customer_email}</div>
                    {reservation.customer_phone && <div className="mt-1 flex items-center gap-2 text-sm font-semibold text-slate-500"><FaPhone /> {reservation.customer_phone}</div>}
                  </div>
                </div>

                <div className="mt-6 flex flex-wrap gap-3">
                  <button disabled={actionLoading || isCancelled(reservation)} onClick={resendConfirmation} className="inline-flex items-center gap-2 rounded-2xl bg-blue-50 px-5 py-3 text-sm font-black text-blue-700 hover:bg-blue-100 disabled:opacity-50">
                    <FaPaperPlane /> Renvoyer confirmation
                  </button>
                  <button disabled={actionLoading || isCancelled(reservation)} onClick={cancelReservation} className="inline-flex items-center gap-2 rounded-2xl bg-red-50 px-5 py-3 text-sm font-black text-red-700 hover:bg-red-100 disabled:opacity-50">
                    <FaTimesCircle /> Annuler
                  </button>
                </div>
              </section>

              <aside className="rounded-[2rem] border border-slate-100 bg-white p-6 shadow-sm">
                <div className="mb-5 inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-950 text-white"><FaMapMarkerAlt /></div>
                <div className="text-[10px] font-black uppercase tracking-widest text-slate-400">Lieu révélé</div>
                <h3 className="mt-2 text-2xl font-black text-slate-950">{reservation.business?.name || 'Partenaire Forfeo'}</h3>
                <p className="mt-2 text-sm font-semibold leading-6 text-slate-500">{[reservation.business?.address, reservation.business?.city, reservation.business?.region].filter(Boolean).join(', ') || 'Adresse disponible dans le courriel de confirmation.'}</p>
                {reservation.business?.phone && <p className="mt-2 flex items-center gap-2 text-sm font-bold text-slate-600"><FaPhone /> {reservation.business.phone}</p>}
                {mapsUrl && (
                  <a href={mapsUrl} target="_blank" rel="noreferrer" className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-slate-950 px-5 py-4 text-sm font-black text-white hover:bg-slate-800">
                    Ouvrir Google Maps <FaExternalLinkAlt />
                  </a>
                )}
                <div className="mt-5 rounded-2xl border border-amber-100 bg-amber-50 p-4 text-xs font-semibold leading-6 text-amber-800">
                  <FaExclamationTriangle className="mb-2" /> Présente ce courriel ou ton nom à l’arrivée. Les disponibilités Last Call sont limitées.
                </div>
              </aside>
            </div>
          ) : (
            <div className="mt-6 rounded-3xl bg-white p-12 text-center shadow-sm">
              <FaExclamationTriangle className="mx-auto mb-4 text-5xl text-amber-400" />
              <h2 className="text-2xl font-black text-slate-900">Réservation introuvable</h2>
              <p className="mt-2 text-sm font-semibold text-slate-500">Vérifie le courriel utilisé au moment de la réservation.</p>
              <Link to="/last-call" className="mt-6 inline-flex items-center gap-2 rounded-2xl bg-red-600 px-6 py-3 font-black text-white hover:bg-red-700">Voir les Last Call</Link>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
